import React, {
  forwardRef,
  ForwardRefExoticComponent,
  Key,
  memo,
  PropsWithoutRef,
  RefAttributes,
  RefObject,
} from 'react';

import {ChannelModel} from '../../../../../libs/shared/core/src/models/api/channelModel';
import {MessageTypesEnum} from '../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {ConversationActionContextsEnum} from '../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {ResourceMap} from '../../../../../libs/shared/core/src/store/commonAppTypes';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TimelineVariationsEnum} from '../../../../../libs/shared/core/src/store/timelineVariations/timelineVariationsTypes';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../../models/api/activityModel';
import {ReminderModel} from '../../../../models/api/reminderModel';
import {getHasLinkedConversations} from '../../../../store/data/conversations/linkedConversationsSelectors';
import {ActivityViewerState} from '../../../../store/panels/viewer/activity/activityViewerState';
import {MessageViewerEducationTipCoordinator} from '../../../onboarding/sandbox/educationTips/messageViewerEducationTipCoordinator';
import {BasicViewerContainer} from './basic/basicViewerContainer';
import {CommentViewerContainer} from './comment/commentViewerContainer';
import {LinkedConversationsViewerContainer} from './linkedConversation/linkedConversationsViewerContainer';
import {MessageViewerContainer} from './message/messageViewerContainer';

/*
 * Props.
 */

interface ActivityViewerProps {
  panelId: ViewerPanelId;
  userId: number;
  cellsContext: CellsContext;
  conversationId: number;
  conversationActionContext: ConversationActionContextsEnum;
  isSpam: boolean;
  namespace: string;
  replyChannel?: ChannelModel;
  isLastMessage?: boolean;
  canceledReminder?: ReminderModel;
  activities: ReadonlyArray<ActivityModel>;
  activityStates: ResourceMap<ActivityViewerState>;
  activityIndex: number;
  containsNewCalendarEvent?: boolean;
  isUnread: boolean;
  isNewUnread: boolean;
  firstMessageActivityIds: ReadonlyArray<number> | undefined;
  isFirstMessage: boolean;
  isFirstComment: boolean;
  isLastActivity: boolean | undefined;
  isFirstActivity: boolean;
  isOnlyMessage: boolean;
  isOnlyActivity: boolean;
  isVisible?: boolean;
  focusKey: Key | undefined;
  jumpKey: Key | undefined;
  jumpSourceActivityId: number | undefined;
  isHighlighted: boolean;
  lastIncomeMessageActivityId?: number;
  timelineVariation: TimelineVariationsEnum;
  /** Used by VariableHeightScrollerBase to ensure we're only referring to activities in the scroller viewport. */
  shouldComputeScrollerVisibility: boolean;
  ref: (ref: RefObject<HTMLDivElement> | undefined) => void;
  isReviewingAiTags?: boolean;
  lastSeenMessageUid?: string;
  conversationMessageType: MessageTypesEnum;
}

/*
 * Component.
 */

export const ActivityViewer = memo<
  ForwardRefExoticComponent<PropsWithoutRef<ActivityViewerProps> & RefAttributes<HTMLDivElement>>
>(
  forwardRef<HTMLDivElement, ActivityViewerProps>((props, scrollerVisibilityRef) => {
    const {
      userId,
      activities,
      activityStates,
      activityIndex,
      lastIncomeMessageActivityId,
      // TODO: Determine whether we still care about `MessageViewer#maybeRenderIntegrationInformationBanner`, which
      // depends on the `isFirstMessage` prop. This props is no longer being passed down into `MessageViewer`!
      isFirstMessage,
      ...otherProps
    } = props;

    const hasLinkedConversations = useAppSelector(getHasLinkedConversations);
    const activity = activities[activityIndex];
    if (!activity) {
      throw new Error('An activity could not be found for this index.');
    }

    switch (activity.visualType) {
      case ActivityVisualTypesEnum.MESSAGE: {
        const previousActivity = activities[activityIndex - 1];
        const previousMessageUid = toMessageUid(previousActivity);

        const lastIncomeMessageActivity = activities.find(
          (activityItem) => activityItem.id === lastIncomeMessageActivityId,
        );
        const lastIncomeMessageUid = toMessageUid(lastIncomeMessageActivity);

        return (
          <MessageViewerEducationTipCoordinator
            conversationId={activity.conversationId}
            activityId={activity.id}
            firstMessageActivityIds={props.firstMessageActivityIds}
          >
            <MessageViewerContainer
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...otherProps}
              messageUid={activity.messageUid}
              activityId={activity.id}
              state={activityStates[activity.id]}
              previousMessageUid={previousMessageUid}
              lastIncomeMessageUid={lastIncomeMessageUid}
              scrollerVisibilityRef={scrollerVisibilityRef}
              isReviewingAiTags={props.isReviewingAiTags}
            />
          </MessageViewerEducationTipCoordinator>
        );
      }

      case ActivityVisualTypesEnum.COMMENT:
        return (
          <CommentViewerContainer
            activity={activity}
            conversationId={props.conversationId}
            isLastSendingComment={false}
            isNewUnread={Boolean(props.isNewUnread)}
            isVisible={Boolean(props.isVisible)}
            isFirstComment={Boolean(props.isFirstComment)}
            jumpKey={props.jumpKey}
            jumpSourceActivityId={props.jumpSourceActivityId}
            isHighlighted={props.isHighlighted}
            panelId={props.panelId}
            previousActivity={activities[activityIndex - 1]}
            conversationActionContext={props.conversationActionContext}
            cellsContext={props.cellsContext}
            canceledReminder={props.canceledReminder}
            timelineVariation={props.timelineVariation}
          />
        );

      case ActivityVisualTypesEnum.DETAILED:
        if (!hasLinkedConversations) {
          return null;
        }
        return (
          <LinkedConversationsViewerContainer
            panelId={props.panelId}
            activity={activity}
            previousActivity={activities[activityIndex - 1]}
          />
        );

      case ActivityVisualTypesEnum.BASIC:
        return (
          <BasicViewerContainer
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
            activities={props.activities}
            activityStates={props.activityStates}
            activityIndex={props.activityIndex}
            focusKey={props.focusKey}
            activity={activity}
            isPreviousActivityBasic={isPreviousActivityBasic(activities[activityIndex - 1])}
            conversationActionContext={props.conversationActionContext}
            cellsContext={props.cellsContext}
            canceledReminder={props.canceledReminder}
          />
        );

      default:
        return null;
    }
  }),
);

/*
 * Activity helpers.
 */

/** Returns the messageUid of the activity if it's a message. */
function toMessageUid(activity?: ActivityModel) {
  if (!activity || activity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
    return undefined;
  }

  return activity.messageUid;
}

/** Check if the previous activity is a basic activity. */
function isPreviousActivityBasic(previousActivity?: ActivityModel) {
  return Boolean(previousActivity && previousActivity.visualType === ActivityVisualTypesEnum.BASIC);
}
